import { omit, shuffle } from 'es-toolkit';

import type { Choice } from '@/api/gen/_model/choice';
import type { EssaySet } from '@/api/gen/_model/essaySet';
import type { Item } from '@/api/gen/_model/item';
import type { MatchSet } from '@/api/gen/_model/matchSet';
import type { MultipleSet } from '@/api/gen/_model/multipleSet';
import type { OrderSet } from '@/api/gen/_model/orderSet';
import type { OxSet } from '@/api/gen/_model/oxSet';
import type { QuizSetData } from '@/api/gen/_model/quizSetData';
import type { SubjectiveSet } from '@/api/gen/_model/subjectiveSet';

// context에서 quizList 정보를 받을 때 초기 가공을 담당하는 함수
type AdaptedQuizBase = Omit<
  QuizSetData,
  'oxData' | 'subjectiveData' | 'order' | 'multipleData' | 'matchData' | 'type'
> & {
  order: number;
};

type AdaptedMultipleSet = MultipleSet & {
  adaptedChoiceList?: (Choice & { order: number; isAnswer: boolean })[];
};

type AdaptedMultipleQuiz = AdaptedQuizBase & {
  type: 'multiple';
  multipleData: AdaptedMultipleSet;
};

type AdaptedOxQuiz = AdaptedQuizBase & {
  type: 'ox';
  oxData: OxSet;
};

type AdaptedSubjectiveQuiz = AdaptedQuizBase & {
  type: 'subjective';
  subjectiveData: SubjectiveSet;
};

type AdaptedEssayQuiz = AdaptedQuizBase & {
  type: 'essay';
  essayData?: EssaySet;
};
type AdaptedMatchSet = Omit<MatchSet, 'leftItems' | 'rightItems'> & {
  leftItems: (Item & { order: number })[];
  rightItems: (Item & { order: number })[];
};
type AdaptedMatchQuiz = AdaptedQuizBase & {
  type: 'match';
  matchData: AdaptedMatchSet;
};

type AdaptedOrderQuiz = AdaptedQuizBase & {
  type: 'order';
  orderData: OrderSet;
};

export type AdaptedQuiz =
  | AdaptedMultipleQuiz
  | AdaptedOxQuiz
  | AdaptedSubjectiveQuiz
  | AdaptedEssayQuiz
  | AdaptedMatchQuiz
  | AdaptedOrderQuiz;

// 원본 순서가 정답이므로, 학습지 출력용 보기만 섞는다.
const shuffleOrderItems = (orderData: OrderSet): OrderSet => ({
  ...orderData,
  items: shuffle(orderData.items ?? []),
});

const _adaptMultipleQuiz = (
  quiz: QuizSetData,
  index: number,
): AdaptedMultipleQuiz => {
  const { choiceListV2, answerList } = quiz.multipleData as MultipleSet;

  const adaptedMultipleQuiz = {
    ...omit(quiz, ['oxData', 'subjectiveData']),
    type: 'multiple' as const,
    order: index,
    multipleData: {
      ...((quiz.multipleData as MultipleSet) ?? {}),
      adaptedChoiceList: choiceListV2?.map((choice, choiceIndex) => ({
        ...choice,
        order: choiceIndex,
        isAnswer: answerList?.includes(String(choiceIndex)),
      })),
    },
  };

  return adaptedMultipleQuiz;
};

export const quizListDataAdapter = (quizList: QuizSetData[]): AdaptedQuiz[] => {
  return quizList.map((quiz, index) => {
    const type = quiz.type as
      | 'ox'
      | 'subjective'
      | 'multiple'
      | 'essay'
      | 'match'
      | 'order';
    switch (type) {
      case 'ox':
        return {
          ...omit(quiz, ['multipleData', 'subjectiveData', 'order']),
          type: 'ox',
          order: index,
        } as AdaptedOxQuiz;
      case 'subjective':
        return {
          ...omit(quiz, ['multipleData', 'oxData', 'order']),
          type: 'subjective',
          order: index,
        } as AdaptedSubjectiveQuiz;
      case 'essay':
        return {
          ...omit(quiz, ['multipleData', 'oxData', 'subjectiveData', 'order']),
          type: 'essay',
          order: index,
        } as AdaptedEssayQuiz;
      case 'match':
        return {
          ...omit(quiz, [
            'multipleData',
            'oxData',
            'subjectiveData',
            'essayData',
            'order',
          ]),
          type: 'match',
          order: index,
          matchData: {
            ...(quiz.matchData as MatchSet),
            leftItems: (quiz.matchData as MatchSet).leftItems.map(
              (item, itemIndex) => ({
                ...item,
                order: itemIndex,
              }),
            ),
            rightItems: (quiz.matchData as MatchSet).rightItems.map(
              (item, itemIndex) => ({
                ...item,
                order: itemIndex,
              }),
            ),
          },
        } as AdaptedMatchQuiz;
      case 'order':
        return {
          ...omit(quiz, [
            'multipleData',
            'oxData',
            'subjectiveData',
            'essayData',
            'matchData',
            'order',
          ]),
          type: 'order',
          order: index,
          orderData: shuffleOrderItems(quiz.orderData as OrderSet),
        } as AdaptedOrderQuiz;
      case 'multiple':
        return _adaptMultipleQuiz(quiz, index);
    }
  });
};
